import React, { useEffect, useState } from 'react';
import { useGlobalErrorStore } from '@/stores/globalErrorStore';
import { getPublicRuntimeConfig } from '@/utils/publicRuntimeConfig';

type GlobalErrorBoundaryProps = {
  children: React.ReactNode;
};

type GlobalErrorBoundaryState = {
  error: Error | null;
};

/**
 * Client-side error boundary similar to Next.js error.js:
 * - Catches render/lifecycle errors in the tree below
 * - Renders a fallback with error info and countdown reload
 * - Does not catch event handler errors, async errors, or SSR errors
 */
export class GlobalErrorBoundary extends React.Component<GlobalErrorBoundaryProps, GlobalErrorBoundaryState> {
  constructor(props: GlobalErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): GlobalErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (getPublicRuntimeConfig().dev) {
      console.error('GlobalErrorBoundary caught:', error, errorInfo);
    }
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

type ErrorFallbackProps = {
  error: Error;
};

const RELOAD_COUNTDOWN_SEC = 10;

function ErrorFallback({ error }: ErrorFallbackProps) {
  const [countdown, setCountdown] = useState(RELOAD_COUNTDOWN_SEC);
  const isDev = getPublicRuntimeConfig().dev;
  const message = isDev ? error.message : 'Please try again later.';

  useEffect(() => {
    if (countdown <= 0) {
      window.location.reload();
      return;
    }
    const id = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [countdown]);

  return (
    <div role="alert" className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <div className="flex w-full max-w-md flex-col items-center gap-8 rounded-2xl border border-[var(--foreground)]/10 p-8 shadow-lg">
        <img src="/equipool_logo.svg" alt="EquiPool" className="h-10 w-auto" />
        <div className="flex flex-col items-center gap-3 text-center text-black">
          <h1 className="text-xl font-semibold">{isDev ? 'Error' : 'Oops! Something went wrong'}</h1>
          <p className="text-sm">{message}</p>
          <p className="text-sm">
            Reloading in {countdown} second{countdown !== 1 ? 's' : ''}…
          </p>
        </div>
        {isDev && error.stack && (
          <pre className="max-h-32 w-full overflow-auto rounded-lg border border-red-200 bg-red-50 p-3 text-left text-xs text-red-700">
            {error.stack}
          </pre>
        )}
      </div>
    </div>
  );
}

function useGlobalErrorListeners() {
  const setError = useGlobalErrorStore((s) => s.setError);

  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      const error = event.error instanceof Error ? event.error : new Error(event.message);
      setError(error);
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      setError(error);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [setError]);
}

/**
 * Wraps children with:
 * - Listeners for window 'error' (sync/event handler errors) and 'unhandledrejection' (async errors)
 * - React error boundary for render/lifecycle errors
 * Shows the same fallback for any of these.
 */
export function GlobalErrorHandler({ children }: { children: React.ReactNode }) {
  const globalError = useGlobalErrorStore((s) => s.error);

  useGlobalErrorListeners();

  if (globalError) {
    return <ErrorFallback error={globalError} />;
  }

  return <GlobalErrorBoundary>{children}</GlobalErrorBoundary>;
}
